import { USER_ROLES } from 'app/models/user/user.constants';

const CLOSED = { canRead: false, canWrite: false, readOnly: true };
const OPEN = { canRead: true, canWrite: true, readOnly: false };

export function isEditorialResource(resource) {
  return ['program', 'content'].includes(resource?.type) && typeof resource.guid === 'string' && resource.guid.length > 0;
}

// Solo un R07 puede estar restringido: el resto de roles, y el R07 sin perfil activo, conservan su acceso
// sin depender de que /editorial-access/me responda ni de la revalidación por foco (TAN-10366: «las
// políticas no alteran R04 ni R07 sin perfil»).
export function getEditorialCapabilities(access, resources = []) {
  if (!access?.role) return CLOSED;
  if (access.role !== USER_ROLES.publisher) return OPEN;
  if (access.status !== 'ready' || !access.data) return CLOSED;
  if (access.data.restricted !== true) return OPEN;
  const writeReady = access.revalidating !== true && Array.isArray(resources) && !resources.some((resource) => resource?.revalidating === true);
  if (!access.data.feature_enabled || !access.data.program_access_enforced) return CLOSED;
  if (!Array.isArray(resources) || !resources.length || resources.some((resource) => !isEditorialResource(resource) || resource.status !== 'allowed')) return CLOSED;
  if (access.data.access_level === 'editor') return { canRead: true, canWrite: writeReady, readOnly: !writeReady };
  // Un Revisor sin `reviewer_write_enforced` es el rollout a medias descrito en editorial-reviewer-rollout.md:
  // el perfil ya está activo y el backend aún no fuerza la restricción de escritura. Ahí el fallback es
  // leer, nunca quedarse sin acceso a nada.
  if (access.data.access_level === 'reviewer') return { canRead: true, canWrite: false, readOnly: true };
  return CLOSED;
}

// La revalidación por foco es un estado de presentación: no cambia quién eres ni a qué tienes acceso.
// Estas capacidades ignoran esa pausa para que el ciclo de vida de una escritura no la confunda con
// una denegación.
export function getSettledEditorialCapabilities(access, resources = []) {
  const settled = Array.isArray(resources) ? resources.map((resource) => ({ ...resource, revalidating: false })) : resources;
  return getEditorialCapabilities({ ...access, revalidating: false }, settled);
}

// Identidad estable del ámbito editorial: cambia cuando cambia el permiso, no cuando se revalida.
export function editorialScopeKey(access, resources = []) {
  const data = access?.data;
  return JSON.stringify({
    role: access?.role ?? null,
    status: access?.status ?? null,
    restricted: data?.restricted ?? null,
    level: data?.access_level ?? null,
    feature: data?.feature_enabled ?? null,
    programs: data?.program_access_enforced ?? null,
    reviewer: data?.reviewer_write_enforced ?? null,
    resources: (Array.isArray(resources) ? resources : []).map((resource) => ({
      type: resource?.type ?? null,
      guid: resource?.guid ?? null,
      status: resource?.status ?? null,
    })),
  });
}

export function getEditorialRouteAction(pathname) {
  if (/^\/(?:program\/(?:create|[^/]+\/book-import)(?:\/|$)|fixedlayout-editor(?:demo)?\/[^/]+(?:\/|$))/.test(pathname)) return 'write';
  return 'read';
}
